import {
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import { Handler, useDrag } from "@use-gesture/react";
import classNames from "classnames";
import copyToClipboard from "copy-to-clipboard";
import { action, isObservable, untracked } from "mobx";
import { observer } from "mobx-react-lite";
import React, {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ActionType, JSONValue, PaneType } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
} from "../ui/Menu";
import { Tooltip } from "../ui/Tooltip";
import {
  areArraysEqual,
  generatePaneId,
  getParentByClassName,
  isModifierKeyDown,
  modifierKey,
} from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canCreateNewPaneComputed,
  canvasTransformComputed,
  canvasViewport,
} from "./CanvasState";
import {
  ArrayMapSentinel,
  dragValueAtom,
  handleNewPaneCreate,
  setFocusPaneIdOnMount,
} from "./NewPane";
import { PaneComponentContext } from "./PaneComponentContext";
import {
  convertViewportPositionToCanvasOffset,
  roundPositionToGrid,
} from "./SandboxUtils";

const ExpandClassName =
  "absolute -left-2 text-neutral-100 group-hover:text-neutral-300 transition-colors cursor-pointer";
const CollapseClassName =
  "absolute -left-2 text-neutral-100 group-hover:text-neutral-300 transition-colors cursor-pointer";
const bracketColor = "text-neutral-400";

const NUM_KEYS_INCREMENT = 16;
const STRING_TRUNCATION_LENGTH = 512;

function TruncatedString({ value }: { value: string }) {
  const [lengthToShow, setLengthToShow] = useState(STRING_TRUNCATION_LENGTH);
  return (
    <>
      {value.substring(0, lengthToShow)}
      {value.length > lengthToShow ? (
        <span
          onClick={(e) => {
            if (isModifierKeyDown(e.metaKey, e.ctrlKey)) {
              return;
            }
            setLengthToShow((v) => v * 2);
          }}
          className="text-neutral-300 hover:text-neutral-500 cursor-pointer transition"
        >
          ...
        </span>
      ) : null}
    </>
  );
}

type JSONRenderProps = {
  value: JSONValue;
  level: number;
  index?: number;
  path: (string | number)[];
  initialCollapseLevel: number;
  isAltKeyDown: boolean;
  mouseOverArrayMapPath: (string | number)[] | undefined;
  setMouseOverTokenPath: React.Dispatch<
    React.SetStateAction<(string | number)[] | undefined>
  >;
};
function JSONRender({
  value,
  level,
  index,
  path,
  initialCollapseLevel,
  isAltKeyDown,
  mouseOverArrayMapPath,
  setMouseOverTokenPath,
}: JSONRenderProps) {
  const [numMaxKeys, setNumMaxKeys] = useState<number | false>(() =>
    level > initialCollapseLevel && index !== 0 ? false : NUM_KEYS_INCREMENT
  );
  const isArrayMapExtract =
    mouseOverArrayMapPath !== undefined &&
    path.length === mouseOverArrayMapPath.length &&
    path.every(
      (k, i) =>
        mouseOverArrayMapPath[i] === ArrayMapSentinel ||
        k === mouseOverArrayMapPath[i]
    );

  const tokenClassName = classNames("json-token", {
    "json-token-array-map-extract": isArrayMapExtract,
  });

  if (value === null) {
    return (
      <span className={classNames(tokenClassName, "text-neutral-400")}>
        null
      </span>
    );
  }
  if (value === undefined) {
    return (
      <span className={classNames(tokenClassName, "text-neutral-400")}>
        undefined
      </span>
    );
  }
  if (typeof value === "number") {
    return (
      <span className={tokenClassName}>{isNaN(value) ? "NaN" : value}</span>
    );
  }
  if (typeof value === "string") {
    const str = value.replace(/\n/g, "\\n");
    return (
      <span className={classNames(tokenClassName, "break-words")}>
        <span className="text-neutral-400">"</span>
        {str.length > STRING_TRUNCATION_LENGTH ? (
          <TruncatedString value={str} />
        ) : (
          str
        )}
        <span className="text-neutral-400">"</span>
      </span>
    );
  }
  if (typeof value === "boolean") {
    return <span className={tokenClassName}>{value ? "true" : "false"}</span>;
  }
  if (value instanceof Date) {
    return (
      <span className={classNames(tokenClassName, "text-neutral-400")}>
        {value.toString()}
      </span>
    );
  }
  if (typeof value === "object") {
    const valueKeys = Object.keys(value);
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <span
            className={classNames(
              tokenClassName,
              "cursor-default",
              bracketColor
            )}
          >
            []
          </span>
        );
      }
      const isArrayMapToken =
        mouseOverArrayMapPath !== undefined &&
        areArraysEqual(path, mouseOverArrayMapPath.slice(0, level));
      if (numMaxKeys === false) {
        return (
          <span
            onClick={(e) => {
              if (isModifierKeyDown(e.metaKey, e.ctrlKey)) {
                return;
              }
              setNumMaxKeys(NUM_KEYS_INCREMENT);
            }}
            className={classNames(
              tokenClassName,
              "cursor-pointer inline-block",
              bracketColor,
              { "json-token-array-map": isArrayMapToken }
            )}
          >
            <FeatherIcon
              icon="chevron-right"
              size="md"
              className={ExpandClassName}
            />
            [<span className="text-neutral-400">...</span>]
          </span>
        );
      }
      return (
        <>
          <span
            onClick={(e) => {
              if (isModifierKeyDown(e.metaKey, e.ctrlKey)) {
                return;
              }
              setNumMaxKeys(false);
            }}
            className={classNames(
              tokenClassName,
              "cursor-pointer inline-block",
              bracketColor,
              { "json-token-array-map": isArrayMapToken }
            )}
          >
            <FeatherIcon
              icon="chevron-down"
              size="md"
              className={CollapseClassName}
            />
            {"["}
          </span>
          <div>
            <div className="ml-4">
              {value
                .slice(
                  0,
                  numMaxKeys + 1 === value.length ? value.length : numMaxKeys
                )
                .map((v, i) => (
                  <div data-key={`${i}`} key={i}>
                    {renderJSON({
                      index: i,
                      value: v,
                      level: level + 1,
                      path: [...path, i],
                      initialCollapseLevel,
                      isAltKeyDown,
                      mouseOverArrayMapPath,
                      setMouseOverTokenPath,
                    })}
                  </div>
                ))}
              {value.length > numMaxKeys + 1 ? (
                <div>
                  <button
                    onClick={(e) => {
                      if (isModifierKeyDown(e.metaKey, e.ctrlKey)) {
                        return;
                      }
                      setNumMaxKeys(
                        (v) => (v !== false ? v : 0) + NUM_KEYS_INCREMENT
                      );
                    }}
                    className="text-neutral-400 hover:text-primary-500"
                  >
                    ...more{" "}
                    <span className="text-neutral-300">
                      (showing {numMaxKeys} of {value.length})
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
            <div className={bracketColor}>{"]"}</div>
          </div>
        </>
      );
    }
    let constructorName: string | undefined = value.constructor?.name;
    if (constructorName === "Object") {
      constructorName = undefined;
    }
    if (valueKeys.length === 0) {
      return (
        <>
          <span
            className={classNames(
              tokenClassName,
              "cursor-default",
              bracketColor
            )}
          >
            {constructorName !== undefined ? (
              <span className="mr-1">{constructorName}</span>
            ) : null}
            {"{}"}
          </span>
        </>
      );
    }
    if (numMaxKeys === false) {
      return (
        <>
          <span
            onClick={(e) => {
              if (isModifierKeyDown(e.metaKey, e.ctrlKey)) {
                return;
              }
              setNumMaxKeys(NUM_KEYS_INCREMENT);
            }}
            className={classNames(
              tokenClassName,
              "cursor-pointer inline-block",
              bracketColor
            )}
          >
            <FeatherIcon
              icon="chevron-right"
              size="md"
              className={ExpandClassName}
            />
            {constructorName !== undefined ? (
              <span className="mr-1">{constructorName}</span>
            ) : null}
            {"{"}
            <span className="text-neutral-400">...</span>
            {"}"}
          </span>
        </>
      );
    }
    return (
      <>
        <span
          onClick={(e) => {
            if (isModifierKeyDown(e.metaKey, e.ctrlKey)) {
              return;
            }
            setNumMaxKeys(false);
          }}
          className={classNames(
            tokenClassName,
            "cursor-pointer inline-block",
            bracketColor
          )}
        >
          <FeatherIcon
            icon="chevron-down"
            size="md"
            className={CollapseClassName}
          />
          {constructorName !== undefined ? (
            <span className="mr-1">{constructorName}</span>
          ) : null}
          {"{"}
        </span>
        <div className="ml-4">
          {valueKeys
            .slice(
              0,
              numMaxKeys + 1 === valueKeys.length
                ? valueKeys.length
                : numMaxKeys
            )
            .map((key, i) => (
              <div data-key={`"${key}"`} key={key}>
                <span>{key}</span>
                <span className="text-neutral-400">:</span>
                {renderJSON({
                  index: i,
                  value: (value as { [key: string]: JSONValue })[key],
                  level: level + 1,
                  path: [...path, key],
                  initialCollapseLevel,
                  isAltKeyDown,
                  mouseOverArrayMapPath,
                  setMouseOverTokenPath,
                })}
              </div>
            ))}
          {valueKeys.length > numMaxKeys + 1 ? (
            <div>
              <button
                onClick={(e) => {
                  if (isModifierKeyDown(e.metaKey, e.ctrlKey)) {
                    return;
                  }
                  setNumMaxKeys(
                    (v) => (v !== false ? v : 0) + NUM_KEYS_INCREMENT
                  );
                }}
                className="text-neutral-400 hover:text-primary-500"
              >
                ...more{" "}
                <span className="text-neutral-300">
                  (showing {numMaxKeys} of {valueKeys.length})
                </span>
              </button>
            </div>
          ) : null}
        </div>
        <div className={bracketColor}>{"}"}</div>
      </>
    );
  }
  if (typeof value === "function") {
    return (
      <span className={classNames(tokenClassName, "text-neutral-400")}>
        function
      </span>
    );
  }
  if (typeof value === "symbol") {
    return (
      <span className={classNames(tokenClassName, "text-neutral-400")}>
        {(value as Symbol).toString()}
      </span>
    );
  }
  if (typeof value === "bigint") {
    return (
      <span className={tokenClassName}>
        {(value as BigInt).toString()}
        <span className="">n</span>
      </span>
    );
  }
  throw new Error(`unrecognized value type: ${typeof value}`);
}

const JSONRenderObserver = observer(JSONRender);

function renderJSON(props: JSONRenderProps) {
  if (isObservable(props.value)) {
    return <JSONRenderObserver {...props} />;
  }
  return <JSONRender {...props} />;
}

export function StringRenderOutput({
  paneId,
  value,
}: {
  paneId: string;
  value: any;
}) {
  return (
    <>
      <div className="p-2 whitespace-pre-wrap">
        {typeof value === "string" ? (
          value
        ) : (
          <div className="text-neutral-400">
            <div>value is not a string</div>
            <div>
              <button
                onClick={() => {
                  canvasDispatch([
                    ActionType.SetPaneRenderOutput,
                    {
                      paneId,
                      renderOutput: undefined,
                    },
                  ]);
                }}
                className="text-primary-400 hover:text-primary-500 transition"
              >
                switch to default renderer
              </button>
            </div>
          </div>
        )}
      </div>
      <DropdownMenuRoot>
        <DropdownMenuTrigger asChild={true}>
          <button className={ContextMenuButtonClassName}>
            <FeatherIcon icon="more-horizontal" size="sm" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent side="bottom" sideOffset={4} align="end">
            <DropdownMenuItem
              onClick={() => {
                copyToClipboard(value);
              }}
            >
              copy string
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenuRoot>
    </>
  );
}

const ValueMenuContent = observer(
  forwardRef(
    (
      {
        paneId,
        canChangeRenderOutput,
        value,
      }: { paneId: string; canChangeRenderOutput: boolean; value: any },
      forwardedRef: React.Ref<HTMLDivElement> | undefined
    ) => {
      const doesLookLikeStructuredData = useMemo(
        () =>
          Array.isArray(value) &&
          value.length > 1 &&
          typeof value[0] === "object" &&
          typeof value[1] === "object" &&
          value[0] !== null &&
          value[1] !== null &&
          Object.keys(value[0]).length > 0 &&
          areArraysEqual(Object.keys(value[0]), Object.keys(value[1])),
        [value]
      );

      return (
        <DropdownMenuContent
          side="bottom"
          sideOffset={4}
          align="end"
          ref={forwardedRef}
        >
          {canChangeRenderOutput && doesLookLikeStructuredData ? (
            <DropdownMenuItem
              onClick={() => {
                canvasDispatch([
                  ActionType.SetPaneRenderOutput,
                  {
                    paneId,
                    renderOutput: ["table"],
                  },
                ]);
              }}
            >
              render pane as table
            </DropdownMenuItem>
          ) : null}
          {canChangeRenderOutput && typeof value === "string" ? (
            <DropdownMenuItem
              onClick={() => {
                canvasDispatch([
                  ActionType.SetPaneRenderOutput,
                  {
                    paneId,
                    renderOutput: ["text"],
                  },
                ]);
              }}
            >
              render pane as text
            </DropdownMenuItem>
          ) : null}
          {typeof value === "string" ? (
            <DropdownMenuItem
              onClick={() => {
                copyToClipboard(value);
              }}
            >
              copy string
            </DropdownMenuItem>
          ) : null}
          <Tooltip
            content={"stringify value\nas json"}
            side="right"
            sideOffset={4}
          >
            <DropdownMenuItem
              onClick={() => {
                const activePaneState = activePaneStates.get(paneId);
                if (activePaneState === undefined) {
                  return;
                }
                const paneOutput = activePaneState.outputBoxes[0].get();
                if (paneOutput[0] !== "value") {
                  return;
                }
                const value = paneOutput[1];
                const json = JSON.stringify(value);
                const paneRect = activePaneState.rect.get();
                const newPaneId = generatePaneId();
                // rAF needed to avoid FocusScope refocusing the dropdown trigger
                requestAnimationFrame(() => {
                  setFocusPaneIdOnMount(newPaneId);
                  canvasDispatch([
                    ActionType.CreatePane,
                    {
                      paneId: newPaneId,
                      type: PaneType.Evaluate,
                      position: [paneRect[0] + paneRect[2] + 32, paneRect[1]],
                      editorHeight: 32,
                      expression: json,
                    },
                  ]);
                });
              }}
            >
              create pane with value
            </DropdownMenuItem>
          </Tooltip>
        </DropdownMenuContent>
      );
    }
  )
);

const ContextMenuButtonClassName =
  "absolute top-2 right-2 flex p-0.5 bg-neutral-100 rounded-sm transition radix-closed:opacity-0 radix-closed:group-hover:opacity-70 radix-closed:group-hover:hover:opacity-100";

function ValueMenuButton({
  paneId,
  canChangeRenderOutput,
  value,
}: {
  paneId: string;
  canChangeRenderOutput: boolean;
  value: any;
}) {
  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger asChild={true}>
        <button className={ContextMenuButtonClassName}>
          <FeatherIcon icon="more-horizontal" size="sm" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <ValueMenuContent
          paneId={paneId}
          canChangeRenderOutput={canChangeRenderOutput}
          value={value}
        />
      </DropdownMenuPortal>
    </DropdownMenuRoot>
  );
}

function getKeyPathForJsonToken(
  element: HTMLElement
): (string | number)[] | undefined {
  const valueNode = getParentByClassName(element, "json-token");
  if (valueNode === undefined) {
    return undefined;
  }
  let node: Element | null = valueNode;
  const path: (string | number)[] = [];
  while (node !== null && node.getAttribute("data-render") !== "root") {
    const nodeKey = node.getAttribute("data-key");
    if (nodeKey !== null) {
      path.unshift(JSON.parse(nodeKey));
    }
    node = node.parentElement;
  }
  return path;
}

export const JSONRenderRoot = observer(
  ({
    paneId,
    value,
    disableDragExtract: disableDragExtractArg = false,
    canChangeRenderOutput = false,
    isLogInspector = false,
    logInspectorName,
    initialCollapseLevel = 2,
    withoutContextMenu,
    keyPathPrefix,
  }: {
    paneId: string;
    value: any;
    disableDragExtract?: boolean;
    canChangeRenderOutput?: boolean;
    isLogInspector?: boolean;
    logInspectorName?: string;
    initialCollapseLevel?: number;
    withoutContextMenu?: boolean;
    keyPathPrefix?: (string | number)[];
  }) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const canCreateNewPane = canCreateNewPaneComputed.get();
    const disableDragExtract = !canCreateNewPane || disableDragExtractArg;
    const { activePaneState } = useContext(PaneComponentContext);
    const [isAltKeyDown, setIsAltKeyDown] = useState(false);
    useEffect(() => {
      if (disableDragExtract) {
        return;
      }
      function onKeyDown(e: KeyboardEvent) {
        if (e.key === modifierKey) {
          rootRef.current!.classList.add("json-root-modifier");
        }
        if (e.key === "Alt") {
          setIsAltKeyDown(true);
        }
      }
      function onKeyUp(e: KeyboardEvent) {
        if (e.key === modifierKey) {
          rootRef.current!.classList.remove("json-root-modifier");
        }
        if (e.key === "Alt") {
          setIsAltKeyDown(false);
        }
      }
      function onBlur() {
        rootRef.current!.classList.remove("json-root-modifier");
        setIsAltKeyDown(false);
      }
      window.addEventListener("keydown", onKeyDown);
      window.addEventListener("keyup", onKeyUp);
      window.addEventListener("blur", onBlur);
      return () => {
        window.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("keyup", onKeyUp);
        window.removeEventListener("blur", onBlur);
      };
    }, [disableDragExtract]);
    const doesLookLikeStructuredData = useMemo(
      () =>
        Array.isArray(value) &&
        value.length > 1 &&
        typeof value[0] === "object" &&
        typeof value[1] === "object" &&
        value[0] !== null &&
        value[1] !== null &&
        Object.keys(value[0]).length > 0 &&
        areArraysEqual(Object.keys(value[0]), Object.keys(value[1])),
      [value]
    );

    const [mouseOverTokenPath, setMouseOverTokenPath] = useState<
      (string | number)[] | undefined
    >(undefined);
    let mouseOverArrayMapPath: (string | number)[] | undefined = undefined;
    if (isAltKeyDown && mouseOverTokenPath !== undefined) {
      for (let i = mouseOverTokenPath.length - 1; i >= 0; i--) {
        if (typeof mouseOverTokenPath[i] === "number") {
          mouseOverArrayMapPath = [...mouseOverTokenPath];
          mouseOverArrayMapPath[i] = ArrayMapSentinel;
          break;
        }
      }
    }

    const bindDrag = useDrag(
      action<Handler<"drag">>(
        ({ first, xy, last, cancel, event, memo = {} }) => {
          if (last) {
            if (dragValueAtom.value !== undefined) {
              handleNewPaneCreate();
              dragValueAtom.value = undefined;
            }
          } else {
            if (first) {
              if (
                disableDragExtract ||
                !(
                  isModifierKeyDown(event.metaKey, event.ctrlKey) ||
                  (event as any).touches?.length === 2
                ) ||
                !(event.target instanceof HTMLElement)
              ) {
                cancel();
                return;
              }
              const path = getKeyPathForJsonToken(event.target);
              if (path === undefined) {
                cancel();
                return;
              }
              memo.keyPath = (keyPathPrefix ?? []).concat(
                mouseOverArrayMapPath !== undefined
                  ? mouseOverArrayMapPath
                  : path
              );
              memo.value = path.reduce((acc, pathItem) => acc[pathItem], value);
              event.preventDefault();
            }
            if (isLogInspector) {
              dragValueAtom.value = {
                type: "value",
                name: logInspectorName!,
                value: memo.value,
                position: xy,
              };
            } else {
              dragValueAtom.value = {
                type: "reference",
                fromPaneId: paneId,
                fromPaneOutputIndex: 0,
                position: xy,
                sourceInfo: {
                  type: "key-path",
                  keyPath: memo.keyPath,
                },
              };
            }
          }
          return memo;
        }
      ),
      {
        transform: ([x, y]) =>
          untracked(() =>
            roundPositionToGrid(
              convertViewportPositionToCanvasOffset(
                [x, y],
                canvasTransformComputed.get(),
                canvasViewport.zoom
              ),
              1
            )
          ),
        pointer: {
          touch: true,
        },
      }
    );

    return (
      <>
        <div
          className={classNames("font-mono-natto relative group", {
            "pl-2": typeof value === "object" && value !== null,
          })}
          data-render="root"
          {...bindDrag()}
          onMouseOver={(e) => {
            if (
              disableDragExtract ||
              !isModifierKeyDown(e.metaKey, e.ctrlKey) ||
              !(e.target instanceof HTMLElement)
            ) {
              return;
            }
            const path = getKeyPathForJsonToken(e.target);
            if (path === undefined) {
              return;
            }
            setMouseOverTokenPath(path);
          }}
          onMouseOut={(e) => {
            if (
              !(
                e.target instanceof HTMLElement &&
                getParentByClassName(e.target, "json-token") !== undefined
              )
            ) {
              return;
            }
            setMouseOverTokenPath(undefined);
          }}
          ref={rootRef}
        >
          {renderJSON({
            value,
            level: 0,
            path: [],
            isAltKeyDown,
            mouseOverArrayMapPath,
            setMouseOverTokenPath,
            initialCollapseLevel,
          })}
        </div>
        {!withoutContextMenu ? (
          <ValueMenuButton
            paneId={paneId}
            canChangeRenderOutput={canChangeRenderOutput}
            value={value}
          />
        ) : null}
      </>
    );
  }
);
